import React from 'react';
import './About.css';
import { motion } from '../utils/motion';

const About = () => {
    const features = [
        {
            icon: '⚡',
            title: 'AI-Powered Innovation',
            desc: 'Leveraging next-gen generative AI pipelines to engineer content that commands viral attention.',
            gradient: 'linear-gradient(135deg, rgba(108, 92, 231, 0.2), rgba(0, 206, 201, 0.1))',
            borderColor: 'rgba(108, 92, 231, 0.4)'
        },
        {
            icon: '🎯',
            title: 'Trend-Focused Strategy',
            desc: 'Constantly tracking algorithmic shifts and culture to stay ahead of global digital trends.',
            gradient: 'linear-gradient(135deg, rgba(253, 121, 168, 0.2), rgba(225, 112, 85, 0.1))',
            borderColor: 'rgba(253, 121, 168, 0.4)'
        },
        {
            icon: '🌍',
            title: 'Global Reach & Engagement',
            desc: 'Crafting universal stories and visuals that transcend boundaries and resonate globally.',
            gradient: 'linear-gradient(135deg, rgba(0, 206, 201, 0.2), rgba(108, 92, 231, 0.1))',
            borderColor: 'rgba(0, 206, 201, 0.4)'
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 40 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
        }
    };

    return (
        <section className="about" id="about">
            <div className="container">
                {/* Section Header */}
                <motion.div 
                    className="about__header"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.8 }}
                >
                    <span className="section-label">About Life Be Like</span>
                    <h2 className="about__title">
                        We turn <span className="about__title-highlight">raw ideas</span> into
                        <br />viral digital experiences
                    </h2>
                    <p className="about__subtitle">
                        A modern digital creation powerhouse combining human artistry with cuting-edge AI technology 
                        to produce content that engages millions across social platforms.
                    </p>
                </motion.div>

                {/* Feature Cards Grid */}
                <motion.div 
                    className="about__features"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                >
                    {features.map((feature, index) => (
                        <motion.div 
                            key={index} 
                            className="about__feature glass-card"
                            variants={cardVariants}
                            whileHover={{ y: -8, scale: 1.02 }}
                            transition={{ type: "spring", stiffness: 300, damping: 20 }}
                            style={{
                                background: feature.gradient,
                                '--hover-border': feature.borderColor
                            }}
                        >
                            <div className="about__feature-icon-wrapper">
                                <span className="about__feature-icon">{feature.icon}</span>
                            </div>
                            <h3 className="about__feature-title">{feature.title}</h3>
                            <p className="about__feature-desc">{feature.desc}</p>
                            <div className="about__feature-glow" />
                        </motion.div>
                    ))}
                </motion.div>

                {/* Glass Mission Banner */}
                <motion.div 
                    className="about__mission glass-card"
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.9 }}
                >
                    <div className="about__mission-content">
                        <div className="about__mission-badge">
                            <span className="section-label">Our Core Mission</span>
                        </div>
                        <p className="about__mission-text">
                            To empower creators, brands, and digital audiences with revolutionary media. 
                            We fuse <strong>artificial intelligence</strong> with storytelling mastery to generate content 
                            that commands attention, sparks conversations, and shapes digital culture.
                        </p>
                        <div className="about__mission-decoration" />
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default About;
